// src/demo/readDemoProfile.js
//
// Synchronous read of the active demo brand profile from URL → localStorage.
// Used by:
//   - useBrandProfile hook (initializer)
//   - Appin.js org-theme effect (to substitute demo colors before derived
//     CSS-var computation)
//
// Keep this stand-alone so the parent effect can read demo state without
// depending on a React hook context.

import { decodeBrandProfile, isValidBrandProfile } from './brandProfile';

export const DEMO_LS_KEY = 'demo:brandProfile';
export const DEMO_ENABLED_LS_KEY = 'demo:enabled';

const SESSION_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes
export const LAST_ACTIVITY_KEY = 'demo:lastActivity';

function isSessionExpired() {
  try {
    const last = localStorage.getItem(LAST_ACTIVITY_KEY);
    if (!last) return false;
    return Date.now() - parseInt(last, 10) > SESSION_TIMEOUT_MS;
  } catch {
    return false;
  }
}

export function bumpDemoActivity() {
  try {
    localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now()));
  } catch {
    /* ignore */
  }
}

// Demo state should only surface on actual booth surfaces. Without this
// gate the localStorage flag (set when a visitor enters the booth and
// retained for the 30-minute session) would leak demo behaviors —
// auto-camera open, demo share overlay on result clicks, retailer
// theme override — to any unrelated catalog visit on the same browser
// within that window. Accepted surfaces:
//   1. Top-level /demo/* routes (the booth's own React surfaces)
//   2. `?demo=true` URL param (set by the booth iframe's src; survives
//      until the catalog's internal redirects strip it)
//   3. Inside an iframe (the booth wraps the catalog in one — internal
//      nav may drop ?demo=true but we're still framed by the booth)
function isDemoSurface() {
  if (typeof window === 'undefined') return false;
  try {
    if (window.location.pathname.startsWith('/demo/')) return true;
    const params = new URLSearchParams(window.location.search);
    if (params.get('demo') === 'true') return true;
    // Cross-origin parents throw on access — that's still an iframe.
    if (window.self !== window.top) return true;
  } catch {
    // Access-denied on `window.top` means we're cross-origin framed;
    // also a valid demo surface.
    return true;
  }
  return false;
}

export function readDemoProfileSync() {
  // Prefer URL param — this is the initialization path that writes the
  // profile to localStorage. Fires regardless of surface gate so a
  // top-level /demo/launch → /demo/booth navigation can persist state.
  if (typeof window !== 'undefined') {
    const params = new URLSearchParams(window.location.search);
    const encoded = params.get('demo_brand');
    if (encoded) {
      const decoded = decodeBrandProfile(encoded);
      if (isValidBrandProfile(decoded)) {
        try {
          localStorage.setItem(DEMO_LS_KEY, JSON.stringify(decoded));
          localStorage.setItem(DEMO_ENABLED_LS_KEY, 'true');
          bumpDemoActivity();
        } catch {
          /* localStorage may be unavailable; ignore */
        }
        return decoded;
      }
    }
  }

  // If the prior session has been idle past the timeout, clear it.
  if (isSessionExpired()) {
    try {
      localStorage.removeItem(DEMO_LS_KEY);
      localStorage.removeItem(DEMO_ENABLED_LS_KEY);
      localStorage.removeItem(LAST_ACTIVITY_KEY);
    } catch {
      /* ignore */
    }
    return null;
  }

  // localStorage fallback — gated to actual demo surfaces. Without
  // this, any user whose browser hit the booth in the last 30 minutes
  // would carry demo styling/behaviors into unrelated catalog visits.
  if (!isDemoSurface()) return null;

  try {
    const stored = localStorage.getItem(DEMO_LS_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (isValidBrandProfile(parsed)) {
        // Mirror decodeBrandProfile's logo restore on the LS path —
        // demo:brandProfile is stored slimmed (no logo) for size, with
        // the heavy data URI on a sibling key so we can splice it back
        // here. Without this, OrganizationLogoNav falls back to the
        // wordmark even when a logo was scraped.
        if (parsed.domain && !parsed.logoDataUri) {
          try {
            const logo = localStorage.getItem(`demo:brandLogo:${parsed.domain}`);
            if (logo) parsed.logoDataUri = logo;
          } catch { /* ignore */ }
        }
        return parsed;
      }
    }
  } catch {
    /* ignore */
  }

  return null;
}
